/**
 * Utility to parse and validate window.__APP_DATA__ for server-side rendered initial data
 */
import { registerCustomResolutions } from './base';
import { normalizeAppData } from './normalizeAppData';

export interface AppData {
  apiUrl?: string;
  /** Screen ID. Legacy payloads send a number, 0.22.x a string. */
  screen: string | number;
  data: {
    variable_data?: any;
    parameters?: any;
    mode?: string;
    config?: {
      basic?: string;
      variables?: string;
      public_url?: string;
      endpoint_id?: string;
      process_id?: string;
      endpoint_type?: string;
      task_ref?: string;
      /** Actions-mode switch. Defaulted to `{ processes: true }` when absent — see normalizeAppData. */
      features?: { processes?: boolean };
      /** Complex-component templates: a top-level sibling in 0.22.x, folded in here. */
      templates?: Record<string, any>;
      urls?: {
        [key: string]: string;
      };
      style?: any;
    };
    screen?: {
      id: string;
      sections: any[];
      settings?: {
        script?: string;
        style?: {
          background?: any[];
        };
        variables?: Record<string, any>;
        base_screen_resolution?: string;
        custom_resolutions?: { id: string; name?: string; width: number; height?: number }[];
        page?: {
          description?: string;
          title?: string;
        };
        skeleton_cache?: boolean;
      };
    };
    live_updates?: boolean;
    localization?: string;
  };
}

/**
 * Check if window.__APP_DATA__ exists and is valid
 */
export const hasInitialData = (): boolean => {
  return typeof window !== 'undefined' && 
         typeof (window as any).__APP_DATA__ !== 'undefined' &&
         (window as any).__APP_DATA__ !== null;
};

/**
 * Parse and validate window.__APP_DATA__
 */
export const parseInitialData = (): AppData | null => {
  if (!hasInitialData()) {
    return null;
  }

  try {
    // tnbe-web-be 0.22.x serves a FLAT envelope (no `data` wrapper, `screen`
    // holds the screen object, `config` renamed to `session_data`). Normalize
    // into the canonical shape FIRST so the validation below — and every
    // consumer downstream — only ever sees one shape.
    const appData = normalizeAppData((window as any).__APP_DATA__) as AppData | null;
    
    // Validate required fields
    if (!appData || !appData.screen || !appData.data) {
      console.warn('Invalid __APP_DATA__: missing required fields');
      return null;
    }

    return appData;
  } catch (error) {
    console.error('Failed to parse __APP_DATA__:', error);
    return null;
  }
};

/**
 * Transform initial data to match the page state structure
 */
export const transformInitialDataToPageState = (appData: AppData) => {
  const data = appData?.data;
  const screen = data?.screen;

  // Guard FIRST: a process/actions payload (or an envelope we could not
  // normalize) carries no screen, and everything below dereferences it.
  if (!screen) {
    return null;
  }
  
  // Unescape the script content if present (it's escaped during save as <\\/script>)
  const unescapedScript = screen?.settings?.script 
    ? screen.settings.script.replace(/<\\\/script>/gi, '</script>')
    : screen?.settings?.script;
  
  // Normalize resolution fields: backend sends snake_case
  // (base_screen_resolution, custom_resolutions) but components read camelCase.
  // Register custom resolution widths so breakpoints keyed by custom ids resolve.
  const customResolutions = (screen?.settings as any)?.custom_resolutions
    ?? (screen?.settings as any)?.customResolutions;
  registerCustomResolutions(customResolutions);

  const enhancedSettings = {
    ...screen?.settings,
    script: unescapedScript,
    live_updates: data.live_updates || false,
    baseScreenResolution: screen?.settings?.base_screen_resolution
      ?? (screen?.settings as any)?.baseScreenResolution,
    customResolutions,
  };

  return {
    screenId: appData.screen,
    data: screen.sections || [],
    settings: enhancedSettings,
    variableData: data.variable_data || {},
    mode: data.mode,
  };
};

/**
 * Transform initial data to match the config state structure
 */
export const transformInitialDataToConfigState = (appData: AppData) => {
  const { data } = appData;
  
  if (!data.config) {
    return null;
  }

  return {
    data: data.config,
  };
};

/**
 * Remove the initialization script tag from DOM after data is parsed
 */
export const removeInitScript = (): void => {
  if (typeof document !== 'undefined') {
    const initScript = document.getElementById('init');
    if (initScript && initScript.parentNode) {
      initScript.parentNode.removeChild(initScript);
    }
  }
};

/**
 * Clear window.__APP_DATA__ after it's been consumed
 */
export const clearInitialData = (): void => {
  if (typeof window !== 'undefined') {
    delete (window as any).__APP_DATA__;
  }
};
